import { useState } from 'react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus, RefreshCw, Download, Trash2, Pencil, Send, CheckCircle, Clock,
  AlertCircle, DollarSign, BarChart3, FileText, Users, Upload
} from 'lucide-react';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import PaymentModal from '@/components/accounting/PaymentModal';
import AgingReportModal from '@/components/accounting/AgingReportModal';
import AccountingImportModal from '@/components/accounting/AccountingImportModal';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function AccountingInvoices({ invoices, chartOfAccounts, allAgents, isAdmin, loadData }) {
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceFilters, setInvoiceFilters] = useState({ startDate: '', endDate: '', status: '', agentId: '' });
  const [outstandingInvoices, setOutstandingInvoices] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [showAgingReport, setShowAgingReport] = useState(false);
  const [agingReportData, setAgingReportData] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    client_name: '', client_email: '', client_address: '',
    invoice_date: new Date().toISOString().split('T')[0], due_date: '',
    tax_rate: 0, notes: '', items: [{ description: '', quantity: 1, unit_price: 0 }],
    accounting_account_id: '', agent_id: '', status: 'draft',
    is_recurring: false, recurring_frequency: 'monthly', recurring_end_date: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check', reference: '', notes: ''
  });

  const resetInvoiceForm = () => {
    setInvoiceForm({
      client_name: '', client_email: '', client_address: '',
      invoice_date: new Date().toISOString().split('T')[0], due_date: '',
      tax_rate: 0, notes: '', items: [{ description: '', quantity: 1, unit_price: 0 }],
      accounting_account_id: '', agent_id: '', status: 'draft',
      is_recurring: false, recurring_frequency: 'monthly', recurring_end_date: ''
    });
  };

  const createInvoice = async () => {
    if (!invoiceForm.client_name || invoiceForm.items.length === 0) {
      toast.error('Please fill in client name and add at least one item');
      return;
    }
    const token = getToken();
    try {
      const items = invoiceForm.items.map(item => ({ ...item, amount: item.quantity * item.unit_price }));
      if (editingInvoice) {
        await axios.put(`${API}/api/accounting/invoices/${editingInvoice.id}`, { ...invoiceForm, items }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Invoice updated');
      } else {
        await axios.post(`${API}/api/accounting/invoices`, { ...invoiceForm, items }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Invoice created');
      }
      setShowInvoiceModal(false);
      setEditingInvoice(null);
      resetInvoiceForm();
      loadData();
    } catch (err) {
      toast.error(editingInvoice ? 'Failed to update invoice' : 'Failed to create invoice');
    }
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    // Calculate tax_rate from stored tax_amount if tax_rate is missing
    let taxRate = invoice.tax_rate;
    if ((taxRate === undefined || taxRate === null || taxRate === '') && invoice.subtotal > 0 && invoice.tax_amount > 0) {
      taxRate = Math.round((invoice.tax_amount / invoice.subtotal) * 10000) / 100;
    }
    setInvoiceForm({
      client_name: invoice.client_name, client_email: invoice.client_email || '',
      client_address: invoice.client_address || '', invoice_date: invoice.invoice_date,
      due_date: invoice.due_date, tax_rate: taxRate || 0, notes: invoice.notes || '',
      items: invoice.items.length > 0 ? invoice.items : [{ description: '', quantity: 1, unit_price: 0 }],
      accounting_account_id: invoice.accounting_account_id || '', agent_id: invoice.agent_id || '',
      status: invoice.status, is_recurring: invoice.is_recurring || false,
      recurring_frequency: invoice.recurring_frequency || 'monthly',
      recurring_end_date: invoice.recurring_end_date || ''
    });
    setShowInvoiceModal(true);
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    const token = getToken();
    try {
      await axios.put(`${API}/api/accounting/invoices/${invoiceId}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Invoice marked as ${status}`);
      loadData();
    } catch (err) { toast.error('Failed to update invoice'); }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/api/accounting/invoices/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invoice deleted');
      loadData();
    } catch (err) { toast.error('Failed to delete invoice'); }
  };

  const handleAgentSelectForInvoice = (agentId) => {
    if (!agentId || agentId === 'manual') return;
    const agent = allAgents.find(a => a.id === agentId);
    if (agent) {
      setInvoiceForm({ ...invoiceForm, client_name: agent.name || '', client_email: agent.email || '', client_address: agent.address || '', agent_id: agentId });
      toast.success(`Client info populated from ${agent.name}`);
    }
  };

  const addInvoiceItem = () => setInvoiceForm({ ...invoiceForm, items: [...invoiceForm.items, { description: '', quantity: 1, unit_price: 0 }] });
  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };
  const removeInvoiceItem = (index) => {
    const newItems = invoiceForm.items.filter((_, i) => i !== index);
    setInvoiceForm({ ...invoiceForm, items: newItems.length > 0 ? newItems : [{ description: '', quantity: 1, unit_price: 0 }] });
  };
  const calculateInvoiceTotal = () => {
    const subtotal = invoiceForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = subtotal * (invoiceForm.tax_rate / 100);
    return { subtotal, tax, total: subtotal + tax };
  };

  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/api/accounting/invoices/${invoiceId}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch (err) { toast.error('Failed to download PDF'); }
  };

  const emailInvoice = async (invoiceId) => {
    const token = getToken();
    try {
      const response = await axios.post(`${API}/api/accounting/invoices/${invoiceId}/email`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send email'); }
  };

  const filterInvoices = async () => {
    // Client-side filtering is applied via filteredInvoices below
    // No API call needed since all invoices are already loaded
    // This just triggers a re-render
    setInvoiceFilters({ ...invoiceFilters });
  };

  // Apply filters client-side
  const filteredInvoices = invoices.filter(inv => {
    if (invoiceFilters.status && inv.status !== invoiceFilters.status) return false;
    if (invoiceFilters.agentId && inv.agent_id !== invoiceFilters.agentId) return false;
    if (invoiceFilters.startDate && inv.invoice_date < invoiceFilters.startDate) return false;
    if (invoiceFilters.endDate && inv.invoice_date > invoiceFilters.endDate) return false;
    return true;
  });

  const loadOutstandingInvoices = async () => {
    const token = getToken();
    try {
      const params = new URLSearchParams();
      if (invoiceFilters.agentId) params.append('agent_id', invoiceFilters.agentId);
      const response = await axios.get(`${API}/api/accounting/invoices/outstanding?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setOutstandingInvoices(response.data);
    } catch (err) { toast.error('Failed to load outstanding invoices'); }
  };

  const downloadOutstandingStatement = async (agentId = null) => {
    const token = getToken();
    try {
      const params = new URLSearchParams();
      if (agentId) params.append('agent_id', agentId);
      else if (invoiceFilters.agentId) params.append('agent_id', invoiceFilters.agentId);
      const response = await axios.get(`${API}/api/accounting/invoices/statement/outstanding?${params}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const agentName = agentId ? (allAgents.find(a => a.id === agentId)?.name || 'agent').replace(/\s+/g, '_') : 'All';
      link.setAttribute('download', `Outstanding_Statement_${agentName}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Outstanding statement downloaded');
    } catch (err) { toast.error('Failed to download statement'); }
  };

  // Get agents that have outstanding invoices
  const agentsWithOutstanding = allAgents.filter(agent =>
    invoices.some(inv => inv.agent_id === agent.id && inv.status !== 'paid')
  );

  const sendInvoiceReminders = async (daysBefore = 7) => {
    const token = getToken();
    try {
      const response = await axios.post(`${API}/api/accounting/invoices/send-reminders?days_before=${daysBefore}&include_overdue=true`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      if (response.data.failed_count > 0) toast.warning(`${response.data.failed_count} reminder(s) failed to send`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send reminders'); }
  };

  const updateOverdueInvoices = async () => {
    const token = getToken();
    try {
      const response = await axios.post(`${API}/api/accounting/invoices/update-overdue`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      loadData();
    } catch (err) { toast.error('Failed to update overdue invoices'); }
  };

  const generateRecurringInvoices = async () => {
    const token = getToken();
    try {
      const response = await axios.post(`${API}/api/accounting/invoices/generate-recurring`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      loadData();
    } catch (err) { toast.error('Failed to generate recurring invoices'); }
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoiceForPayment(invoice);
    setPaymentForm({ amount: invoice.balance_due || invoice.total, payment_date: new Date().toISOString().split('T')[0], payment_method: 'check', reference: '', notes: '' });
    setShowPaymentModal(true);
  };

  const recordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    const token = getToken();
    try {
      const response = await axios.post(`${API}/api/accounting/invoices/${selectedInvoiceForPayment.id}/payments`, {
        amount: parseFloat(paymentForm.amount), payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method, reference: paymentForm.reference, notes: paymentForm.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      setShowPaymentModal(false);
      setSelectedInvoiceForPayment(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
  };

  const loadAgingReport = async () => {
    const token = getToken();
    try {
      const params = new URLSearchParams();
      if (invoiceFilters.agentId) params.append('agent_id', invoiceFilters.agentId);
      const response = await axios.get(`${API}/api/accounting/invoices/aging-report?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setAgingReportData(response.data);
      setShowAgingReport(true);
    } catch (err) { toast.error('Failed to load aging report'); }
  };

  const downloadAgingReportPdf = async () => {
    const token = getToken();
    try {
      const params = new URLSearchParams();
      if (invoiceFilters.agentId) params.append('agent_id', invoiceFilters.agentId);
      const response = await axios.get(`${API}/api/accounting/invoices/aging-report/pdf?${params}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Aging_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Aging report downloaded');
    } catch (err) { toast.error('Failed to download aging report'); }
  };

  return (
    <>
      {/* Invoice Filters */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium">Filter & Actions</CardTitle>
        </CardHeader>
        <CardContent className="py-2">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={invoiceFilters.startDate} onChange={(e) => setInvoiceFilters({ ...invoiceFilters, startDate: e.target.value })} className="w-36 h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={invoiceFilters.endDate} onChange={(e) => setInvoiceFilters({ ...invoiceFilters, endDate: e.target.value })} className="w-36 h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={invoiceFilters.status || "all"} onValueChange={(v) => setInvoiceFilters({ ...invoiceFilters, status: v === "all" ? "" : v })}>
                <SelectTrigger className="w-32 h-9"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="partially_paid">Partially Paid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Agent</Label>
              <Select value={invoiceFilters.agentId || "all"} onValueChange={(v) => setInvoiceFilters({ ...invoiceFilters, agentId: v === "all" ? "" : v })}>
                <SelectTrigger className="w-40 h-9"><SelectValue placeholder="All Agents" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {allAgents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={filterInvoices} data-testid="filter-invoices-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Filter
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setInvoiceFilters({ startDate: '', endDate: '', status: '', agentId: '' }); loadData(); }}>Clear</Button>
            <div className="ml-auto flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={loadAgingReport} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="aging-report-btn">
                <BarChart3 className="w-4 h-4 mr-1" /> Aging Report
              </Button>
              <Button size="sm" variant="outline" onClick={loadOutstandingInvoices} className="border-amber-300 text-amber-700 hover:bg-amber-50">
                <AlertCircle className="w-4 h-4 mr-1" /> Outstanding
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="statement-pdf-dropdown">
                    <Download className="w-4 h-4 mr-1" /> Statement PDF
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
                  <DropdownMenuLabel className="flex items-center gap-2 text-xs">
                    <FileText className="w-3.5 h-3.5" /> Outstanding Statement PDF
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => downloadOutstandingStatement()} data-testid="statement-pdf-all">
                    <Users className="w-4 h-4 mr-2" /> All Agents
                  </DropdownMenuItem>
                  {agentsWithOutstanding.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-slate-500">Per Agent</DropdownMenuLabel>
                      {agentsWithOutstanding.map(agent => (
                        <DropdownMenuItem key={agent.id} onClick={() => downloadOutstandingStatement(agent.id)} data-testid={`statement-pdf-${agent.id}`}>
                          {agent.name}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {isAdmin && (
                <>
                  <Button size="sm" variant="outline" onClick={() => sendInvoiceReminders(7)} className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="send-reminders-btn" title="Send reminders to invoices due within 7 days">
                    <Send className="w-4 h-4 mr-1" /> Send Reminders
                  </Button>
                  <Button size="sm" variant="outline" onClick={updateOverdueInvoices} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="update-overdue-btn">
                    <Clock className="w-4 h-4 mr-1" /> Update Overdue
                  </Button>
                  <Button size="sm" variant="outline" onClick={generateRecurringInvoices} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="generate-recurring-btn">
                    <RefreshCw className="w-4 h-4 mr-1" /> Generate Recurring
                  </Button>
                </>
              )}
            </div>
          </div>

          {outstandingInvoices && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex gap-6">
                  <div><p className="text-xs text-amber-600">Outstanding Invoices</p><p className="text-lg font-bold text-amber-700">{outstandingInvoices.invoice_count}</p></div>
                  <div><p className="text-xs text-amber-600">Total Outstanding</p><p className="text-lg font-bold font-mono text-amber-700">{formatCurrency(outstandingInvoices.total_outstanding)}</p></div>
                  <div><p className="text-xs text-red-600">Overdue</p><p className="text-lg font-bold text-red-700">{outstandingInvoices.overdue_count}</p></div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setOutstandingInvoices(null)}>✕</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Invoices</CardTitle>
            <CardDescription>Manage customer invoices - linked to transactions and agent expenses</CardDescription>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowImportModal(true)} data-testid="import-invoices-btn">
                <Upload className="w-4 h-4 mr-2" /> Import
              </Button>
              <Button onClick={() => { resetInvoiceForm(); setEditingInvoice(null); setShowInvoiceModal(true); }} data-testid="create-invoice-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Invoice
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-500">{invoiceFilters.status || invoiceFilters.agentId || invoiceFilters.startDate || invoiceFilters.endDate ? 'No invoices match the selected filters' : 'No invoices created yet'}</TableCell></TableRow>
                ) : (
                  filteredInvoices.map(inv => (
                    <TableRow key={inv.id} className={inv.status === 'overdue' || (inv.due_date < new Date().toISOString().split('T')[0] && inv.status !== 'paid' && inv.status !== 'cancelled') ? 'bg-red-50' : ''}>
                      <TableCell className="font-mono">
                        {inv.invoice_number}
                        {inv.is_recurring && <Badge variant="outline" className="ml-1 text-xs bg-blue-50">Recurring</Badge>}
                      </TableCell>
                      <TableCell>{inv.client_name}</TableCell>
                      <TableCell>{inv.agent_name ? <Badge variant="outline" className="text-xs">{inv.agent_name}</Badge> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                      <TableCell className="font-mono text-sm">{formatDate(inv.invoice_date)}</TableCell>
                      <TableCell className="font-mono text-sm">{formatDate(inv.due_date)}</TableCell>
                      <TableCell>{inv.accounting_account_name ? <Badge variant="outline" className="text-xs whitespace-nowrap">{inv.accounting_account_name}</Badge> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-right font-mono font-medium">{formatCurrency(inv.total)}</TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        {inv.status === 'paid' ? <span className="text-emerald-600">Paid</span> : <span className={inv.balance_due > 0 ? 'text-amber-600' : ''}>{formatCurrency(inv.balance_due || inv.total)}</span>}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          inv.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                          inv.status === 'partially_paid' ? 'bg-amber-100 text-amber-800' :
                          inv.status === 'overdue' ? 'bg-red-100 text-red-800' :
                          inv.status === 'sent' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-800'
                        }>{inv.status === 'partially_paid' ? 'Partial' : inv.status}</Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div className="flex gap-1">
                            {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                              <Button variant="ghost" size="sm" onClick={() => openPaymentModal(inv)} title="Record Payment" data-testid={`record-payment-${inv.id}`}>
                                <DollarSign className="w-4 h-4 text-emerald-500" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => downloadInvoicePdf(inv.id, inv.invoice_number)} title="Download PDF">
                              <Download className="w-4 h-4 text-emerald-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => emailInvoice(inv.id)} title="Email to Client" disabled={!inv.client_email}>
                              <Send className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEditInvoice(inv)} data-testid={`edit-invoice-${inv.id}`} title="Edit Invoice">
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                            {inv.status !== 'paid' && (
                              <Button variant="ghost" size="sm" onClick={() => updateInvoiceStatus(inv.id, 'paid')} title="Mark as Paid">
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              </Button>
                            )}
                            {inv.status === 'draft' && (
                              <Button variant="ghost" size="sm" onClick={() => updateInvoiceStatus(inv.id, 'sent')} title="Mark as Sent">
                                <Send className="w-4 h-4 text-blue-500" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => deleteInvoice(inv.id)} data-testid={`delete-invoice-${inv.id}`} title="Delete Invoice">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={(open) => { setShowInvoiceModal(open); if (!open) { setEditingInvoice(null); resetInvoiceForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-modal">
          <DialogHeader><DialogTitle>{editingInvoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
              <Label className="text-blue-700">Quick Fill from Agent</Label>
              <Select onValueChange={handleAgentSelectForInvoice}>
                <SelectTrigger data-testid="agent-select-for-invoice"><SelectValue placeholder="Select an agent to populate client info..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">-- Manual Entry --</SelectItem>
                  {allAgents.map(agent => (<SelectItem key={agent.id} value={agent.id}>{agent.name} {agent.email ? `(${agent.email})` : ''}</SelectItem>))}
                </SelectContent>
              </Select>
              <p className="text-xs text-blue-600">Select an agent to auto-fill client info, or enter manually below</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Client Name *</Label><Input value={invoiceForm.client_name} onChange={(e) => setInvoiceForm({ ...invoiceForm, client_name: e.target.value })} placeholder="Enter client name or select agent above" /></div>
              <div className="space-y-2"><Label>Client Email</Label><Input type="email" value={invoiceForm.client_email} onChange={(e) => setInvoiceForm({ ...invoiceForm, client_email: e.target.value })} placeholder="client@example.com" /></div>
            </div>
            <div className="space-y-2"><Label>Client Address</Label><Textarea value={invoiceForm.client_address} onChange={(e) => setInvoiceForm({ ...invoiceForm, client_address: e.target.value })} rows={2} placeholder="Enter client address" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Invoice Date</Label><Input type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Tax Rate (%)</Label><Input type="number" step="0.01" value={invoiceForm.tax_rate} onChange={(e) => setInvoiceForm({ ...invoiceForm, tax_rate: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div className="space-y-2">
              <Label>Accounting Category</Label>
              <Select value={invoiceForm.accounting_account_id || "none"} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, accounting_account_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="invoice-category-select"><SelectValue placeholder="Select accounting category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {chartOfAccounts.filter(a => a.account_type === 'income').map(acc => (<SelectItem key={acc.id} value={acc.id}>{acc.account_number} - {acc.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Link this invoice to an income account</p>
            </div>
            <div className="space-y-2">
              <Label>Line Items</Label>
              {invoiceForm.items.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input placeholder="Description" className="flex-1" value={item.description} onChange={(e) => updateInvoiceItem(idx, 'description', e.target.value)} />
                  <Input type="number" placeholder="Qty" className="w-20" value={item.quantity} onChange={(e) => updateInvoiceItem(idx, 'quantity', e.target.value)} />
                  <Input type="number" placeholder="Price" className="w-28" value={item.unit_price} onChange={(e) => updateInvoiceItem(idx, 'unit_price', e.target.value)} />
                  <span className="font-mono w-24 text-right">{formatCurrency(item.quantity * item.unit_price)}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeInvoiceItem(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addInvoiceItem}><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{formatCurrency(calculateInvoiceTotal().subtotal)}</span></div>
              <div className="flex justify-between"><span>Tax ({invoiceForm.tax_rate}%)</span><span className="font-mono">{formatCurrency(calculateInvoiceTotal().tax)}</span></div>
              <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span className="font-mono">{formatCurrency(calculateInvoiceTotal().total)}</span></div>
            </div>
            <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_recurring" checked={invoiceForm.is_recurring} onChange={(e) => setInvoiceForm({ ...invoiceForm, is_recurring: e.target.checked })} className="rounded border-slate-300" />
                <Label htmlFor="is_recurring" className="cursor-pointer">Make this a recurring invoice template</Label>
              </div>
              {invoiceForm.is_recurring && (
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select value={invoiceForm.recurring_frequency} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, recurring_frequency: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>End Date (Optional)</Label><Input type="date" value={invoiceForm.recurring_end_date} onChange={(e) => setInvoiceForm({ ...invoiceForm, recurring_end_date: e.target.value })} /></div>
                </div>
              )}
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={invoiceForm.notes} onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} placeholder="Additional notes for the invoice..." rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>Cancel</Button>
            <Button onClick={createInvoice}>Create Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentModal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} invoice={selectedInvoiceForPayment} paymentForm={paymentForm} setPaymentForm={setPaymentForm} onRecordPayment={recordPayment} />
      <AgingReportModal isOpen={showAgingReport} onClose={() => setShowAgingReport(false)} agingReportData={agingReportData} onDownloadPdf={downloadAgingReportPdf} />

      {/* Import Modal */}
      <AccountingImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        importType="invoice"
        onImportComplete={loadData}
      />
    </>
  );
}
